// Create backtest modal — mirrors legacy showBacktestModal / submitBacktest.
// Fire-and-forget POST /backtest; caller relies on polling to see status change.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost } from "@/lib/api-client";
import Modal from "@/components/common/modal";

interface CreateBacktestBody {
  symbol: string;
  lower_price: number;
  upper_price: number;
  grid_count: number;
  quantity_per_grid: number;
  start_date: string;
  end_date: string;
}

const inputCls =
  "w-full bg-bg border border-border rounded px-2.5 py-1.5 text-sm text-text outline-none focus:border-accent/60 transition-colors";
const primaryBtn =
  "text-xs px-4 py-2 rounded bg-accent/20 border border-accent/50 text-accent hover:bg-accent/30 transition-colors disabled:opacity-50";
const ghostBtn =
  "text-xs px-4 py-2 rounded border border-border text-muted hover:text-text transition-colors";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[0.7rem] uppercase tracking-wider text-muted mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function CreateBacktestModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [symbol, setSymbol] = useState("BTC/USDT:USDT");
  const [lower, setLower] = useState("");
  const [upper, setUpper] = useState("");
  const [gridCount, setGridCount] = useState("10");
  const [qty, setQty] = useState("0.001");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState("");

  const mutation = useMutation({
    mutationFn: (body: CreateBacktestBody) =>
      apiPost<{ ok: boolean; backtest_id: number }>("/backtest", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backtests"] });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    const body: CreateBacktestBody = {
      symbol,
      lower_price: parseFloat(lower),
      upper_price: parseFloat(upper),
      grid_count: parseInt(gridCount),
      quantity_per_grid: parseFloat(qty),
      start_date: startDate,
      end_date: endDate,
    };

    if (
      !body.symbol ||
      !body.lower_price ||
      !body.upper_price ||
      !body.grid_count ||
      !body.quantity_per_grid ||
      !body.start_date ||
      !body.end_date
    ) {
      setError("Fill all required fields");
      return;
    }

    mutation.mutate(body);
  }

  return (
    <Modal open onClose={onClose} title="New Backtest">
      <form onSubmit={handleSubmit} className="space-y-3">
        <Field label="Symbol">
          <input
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            placeholder="BTC/USDT:USDT"
            className={inputCls}
            required
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Lower Price">
            <input
              type="number"
              step="any"
              value={lower}
              onChange={(e) => setLower(e.target.value)}
              placeholder="60000"
              className={inputCls}
              required
            />
          </Field>
          <Field label="Upper Price">
            <input
              type="number"
              step="any"
              value={upper}
              onChange={(e) => setUpper(e.target.value)}
              placeholder="70000"
              className={inputCls}
              required
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Grid Count">
            <input
              type="number"
              value={gridCount}
              onChange={(e) => setGridCount(e.target.value)}
              className={inputCls}
              required
            />
          </Field>
          <Field label="Qty per Grid">
            <input
              type="number"
              step="any"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder="0.001"
              className={inputCls}
              required
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Start Date">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={inputCls}
              required
            />
          </Field>
          <Field label="End Date">
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={inputCls}
              required
            />
          </Field>
        </div>

        {error && <div className="text-danger text-xs">{error}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={ghostBtn}>
            Cancel
          </button>
          <button type="submit" disabled={mutation.isPending} className={primaryBtn}>
            {mutation.isPending ? "Queuing…" : "Run Backtest"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
